import React from 'react';
import {
  Box, Chip, Stack, Table, TableBody, TableCell, TableHead, TableRow, Typography,
} from '@mui/material';
import { humanizeKey } from './flowBuilderModel';

const isPlainObject = (v) => v != null && typeof v === 'object' && !Array.isArray(v);
const isPrimitive = (v) => v == null || ['string', 'number', 'boolean'].includes(typeof v);

function Primitive({ value }) {
  if (value == null || value === '') return <Typography variant="body2" color="text.disabled">empty</Typography>;
  if (typeof value === 'boolean') return <Typography variant="body2">{value ? 'Yes' : 'No'}</Typography>;
  return <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{String(value)}</Typography>;
}

function ObjectTable({ rows }) {
  const cols = [...new Set(rows.flatMap((r) => Object.keys(r)))].slice(0, 6);
  return (
    <Box sx={{
      overflowX: 'auto', border: 1, borderColor: 'divider', borderRadius: 1.5,
    }}
    >
      <Table size="small">
        <TableHead>
          <TableRow>
            {cols.map((c) => <TableCell key={c} sx={{ fontWeight: 700 }}>{humanizeKey(c)}</TableCell>)}
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.slice(0, 50).map((r, i) => (
            // eslint-disable-next-line react/no-array-index-key
            <TableRow key={i}>
              {cols.map((c) => (
                <TableCell key={c} sx={{ verticalAlign: 'top' }}>
                  {isPrimitive(r[c]) ? String(r[c] ?? '') : JSON.stringify(r[c])}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Box>
  );
}

/** Renders any JSON value as labelled, readable sections (tables for lists of records). */
export default function RunResultView({ value, depth = 0 }) {
  if (isPrimitive(value)) return <Primitive value={value} />;
  if (Array.isArray(value)) {
    if (!value.length) return <Typography variant="body2" color="text.disabled">none</Typography>;
    if (value.every(isPrimitive)) {
      return (
        <Stack direction="row" gap={0.75} flexWrap="wrap">
          {value.map((v, i) => <Chip key={`${i}-${String(v)}`} size="small" label={String(v)} />)}
        </Stack>
      );
    }
    if (value.every((v) => isPlainObject(v) && Object.values(v).every(isPrimitive))) return <ObjectTable rows={value} />;
    return (
      <Stack spacing={1}>
        {value.map((v, i) => (
          // eslint-disable-next-line react/no-array-index-key
          <Box key={i} sx={{ pl: 1.5, borderLeft: 2, borderColor: 'divider' }}>
            <RunResultView value={v} depth={depth + 1} />
          </Box>
        ))}
      </Stack>
    );
  }
  const entries = Object.entries(value);
  if (!entries.length) return <Typography variant="body2" color="text.disabled">empty</Typography>;
  if (depth > 3) {
    return (
      <Typography component="pre" variant="caption" sx={{ m: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
        {JSON.stringify(value, null, 2)}
      </Typography>
    );
  }
  return (
    <Stack spacing={depth === 0 ? 2 : 1.25}>
      {entries.map(([k, v]) => (
        <Box key={k}>
          <Typography
            variant={depth === 0 ? 'subtitle2' : 'caption'}
            sx={{
              fontWeight: 700, color: depth === 0 ? 'text.primary' : 'text.secondary', display: 'block', mb: 0.25,
            }}
          >
            {humanizeKey(k)}
          </Typography>
          <Box sx={isPrimitive(v) ? undefined : { pl: depth === 0 ? 0 : 1.5, borderLeft: depth === 0 ? 0 : 2, borderColor: 'divider' }}>
            <RunResultView value={v} depth={depth + 1} />
          </Box>
        </Box>
      ))}
    </Stack>
  );
}
